<script setup lang="ts">
import { Skeleton, Markdown } from "@/components/Ui";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useLegalStore } from "@/store/legal";

definePageMeta({
  key: "legal-root"
});
const { t } = useI18n();
const route = useRoute();
const { $getFormatDate } = useNuxtApp();
const { legalItems, pending } = storeToRefs(useLegalStore());

useSeoMeta({
  title: t("page.legal.seo.title"),
  description: t("page.legal.seo.description"),
  ogTitle: t("page.legal.seo.title"),
  ogDescription: t("page.legal.seo.description")
});

</script>

<template>
  <div :class="$style.root">
    <h1>Legal information</h1>
    <div :class="$style.root__desc">
      <p>We believe in the importance of transparency.</p>
      <p>Please review the following information carefully!</p>
    </div>
    <ul>
      <template v-if="!pending">
        <li
          v-for="(item, index) in legalItems"
          :key="index"
        >
          <nuxt-link
            :to="{ path: `/legal/${item.attributes.slug}` }"
            :title="item.attributes.title"
          >
            <client-only>
              <p v-if="item.attributes.updatedAt">
                Last update:
                {{
                  $getFormatDate(
                    item.attributes.updatedAt,
                    "en",
                    "dd MMM yyyy"
                  ).toUpperCase()
                }}
              </p>
            </client-only>
            <Markdown
              :content="item.attributes.title"
              position="nav-main"
            />
          </nuxt-link>
        </li>
      </template>
      <template v-else>
        <li
          v-for="index in 3"
          :key="index"
        >
          <a>
            <Skeleton :options="{ width: 147, height: 17 }">
              <template #loader>
                <rect
                  x="0"
                  y="0"
                  rx="4"
                  ry="4"
                  width="147"
                  height="17"
                />
              </template>
            </Skeleton>
            <span>
              <Skeleton :options="{ width: 320, height: 35 }">
                <template #loader>
                  <rect
                    x="0"
                    y="0"
                    rx="8"
                    ry="8"
                    width="320"
                    height="35"
                  />
                </template>
              </Skeleton>
            </span>
          </a>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" module>
.root {
  @apply flex flex-col gap-[60px] pt-10 md:pt-0;  ;
  & h1 {
    @apply text-neutral-800 text-[50px] xxs:text-[54px] md:text-[120px] leading-none font-extrabold;
  }

  & ul {
    @apply flex flex-col gap-4;
    & li {
      @apply rounded-[16px] overflow-hidden transition-all;
      @apply cursor-pointer bg-neutral-100 hover:bg-neutral-200 hover:bg-opacity-50;

      & a {
        @apply flex flex-col gap-2 px-4 py-5;

        & span p {
          @apply font-semibold text-[24px] leading-snug text-neutral-900 inline-flex xxs:gap-2 items-center flex-wrap;
        }

        & p {
          @apply text-sm text-neutral-500 leading-snug;
        }
      }
    }
  }

  &__desc {
    @apply flex flex-col gap-3;

    & p {
      &:first-child {
        @apply text-neutral-800 text-[20px] md:text-[40px] leading-tight;
      }

      &:last-child {
        @apply text-neutral-500 text-[15px] md:text-2xl leading-snug;
      }
    }
  }
}
</style>
